import cn from 'classnames';
import { uniq, uniqBy } from 'lodash';
import { useRouter } from 'next/router';
import { ReactElement, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import format from 'date-fns/format';

import { Icon, IconButton } from 'components';
import { ButtonV3 } from 'components/ComponentV2';
import { DatePicker } from 'components/DatePickerV2/DatePicker.component';
import Modal from 'components/Modal';
import { TimePicker } from 'components/TimePickerV2/TimePicker.component';
import { DropdownV2 } from 'components/form';

import { useApp } from 'hooks/useApp';
import { useEvent } from 'hooks/useEvent';

import styles from './EventCloneModal.module.scss';

interface EventCloneModalProps {
  isOpen: boolean;
  onClose: () => void;
}
type TimezoneMappings = {
  [key: string]: string; // Assuming that both keys and values are strings
};

export const EventCloneModal = ({
  isOpen,
  onClose,
}: EventCloneModalProps): ReactElement => {
  const [t] = useTranslation('event-dashboard');

  const router = useRouter();
  const { id } = router.query;
  const { cloneEvent } = useEvent();

  const [timezone, setTimezone] = useState('Europe/London');

  const [startDate, setStartDate] = useState<Date>();
  const [startTime, setStartTime] = useState<string>();

  const [endDate, setEndDate] = useState<Date>();
  const [endTime, setEndTime] = useState<string>();
  const [endDateError, setEndDateError] = useState({
    endDate: false,
    endTime: false,
  });
  const [showEndDatePicker, setShowEndDatePicker] = useState<boolean>(false);

  const [deadlineDate, setDeadlineDate] = useState<Date>();
  const [deadlineTime, setDeadlineTime] = useState<string>();
  const [deadlineError, setDeadlineError] = useState({
    deadlineDate: false,
    deadlineTime: false,
  });
  const [showDeadlinePicker, setShowDeadlinePicker] = useState<boolean>(false);

  const { locationList } = useApp();

  const timezoneList = useMemo(() => {
    const uniqueLocations = uniqBy(locationList, (obj) => obj?.country?.name);
    return uniq(
      uniqueLocations
        .map(({ country }) => country?.timezones)
        ?.flatMap((time) => time) || []
    );
  }, [locationList]);

  const timezoneMappings = useMemo(() => {
    let mappings: TimezoneMappings = {};
    const uniqueLocations = uniqBy(locationList, (obj) => obj?.country?.name);
    uniqueLocations.forEach((loc) => {
      mappings = { ...mappings, ...loc?.timezoneMapping };
    });
    return mappings;
  }, [locationList]);

  const timezoneOptions = useMemo(() => {
    return timezoneList.map((data, index) => ({
      id: (index + 1).toString(),
      label: data ? `${data} ${timezoneMappings[data] || ''}` : '',
      value: data || '',
    }));
  }, [timezoneList, timezoneMappings]);

  const timezoneLabel = useMemo(() => {
    return timezoneOptions.find((data) => data.value === timezone)?.label || '';
  }, [timezoneOptions, timezone]);

  const onDone = async () => {
    let hasError = false;
    if (showEndDatePicker) {
      if (!endDate || !endTime) {
        setEndDateError({ endDate: !endDate, endTime: !endTime });
        hasError = true;
      }
    }

    if (showDeadlinePicker) {
      if (!deadlineDate || !deadlineTime) {
        setDeadlineError({
          deadlineDate: !deadlineDate,
          deadlineTime: !deadlineTime,
        });
        hasError = true;
      }
    }

    if (hasError) {
      return;
    }

    const newEvent = await cloneEvent({
      data: {
        startDate:
          format(startDate || new Date(), 'MMM dd yyy') + ' ' + startTime,
        ...(endDate && {
          endDate: format(endDate, 'MMM dd yyy') + ' ' + endTime,
        }),
        ...(deadlineDate && {
          applicationDeadline:
            format(deadlineDate, 'MMM dd yyy') + ' ' + deadlineTime,
        }),
        timezone,
      },
      id: id as string,
    });
    onClose();
    router.push(`/campaigns/${newEvent?.id}`);
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentClassName={styles.container}
    >
      <IconButton
        size="small"
        iconName="close"
        className={styles.cancelIcon}
        onClick={() => onClose()}
      />
      <div
        className={cn(styles.body, {
          [styles.endDate]: showEndDatePicker,
          [styles.deadline]: showDeadlinePicker,
        })}
      >
        <div className={styles.title}>{t('event-clone-modal.title')}</div>
        <div className={styles.content}>
          <div className={styles.row}>
            <div className={styles.column}>
              <p className={styles.label}>
                {t('event-clone-modal.start-date.date')}
              </p>

              <DatePicker
                placeholder={t('event-clone-modal.placeholder.pick-date')}
                value={startDate || null}
                onChange={(value) => {
                  setStartDate(value);
                }}
                className={styles.dropDownCalendar}
                minDate={new Date()}
                dateSelectorClassName={styles.dateSelector}
              />
            </div>
            <div className={styles.column}>
              <p className={styles.label}>
                {t('event-clone-modal.start-date.time')}
              </p>

              <TimePicker
                value={startTime}
                onChange={(value) => setStartTime(value)}
                className={styles.dropdownTimer}
                placeholder={t('event-clone-modal.placeholder.select-time')}
                triggerIcon={
                  <Icon
                    iconName="icon_arrow-down"
                    size="xsmall"
                    className={styles.timeIcon}
                  />
                }
              />
            </div>
          </div>

          {showEndDatePicker ? (
            <div className={styles.row}>
              <div className={styles.column}>
                <p className={styles.label}>
                  {t('event-clone-modal.end-date.date')}
                </p>

                <DatePicker
                  placeholder={t('event-clone-modal.placeholder.pick-date')}
                  value={endDate || null}
                  onChange={(value) => {
                    setEndDate(value);
                    setEndDateError({
                      endDate: false,
                      endTime: endDateError.endTime,
                    });
                  }}
                  className={styles.dropDownCalendar}
                  minDate={startDate}
                  dateSelectorClassName={styles.dateSelector}
                  hasError={endDateError.endDate}
                />
                {endDateError.endDate && (
                  <p className={styles.errorMessage}>
                    {t('event-clone-modal.error-message.pick-valid-date')}
                  </p>
                )}
              </div>
              <div className={styles.column}>
                <p className={styles.label}>
                  {t('event-clone-modal.end-date.time')}
                </p>

                <TimePicker
                  value={endTime}
                  onChange={(value) => {
                    setEndTime(value);
                    setEndDateError({
                      endDate: endDateError.endDate,
                      endTime: false,
                    });
                  }}
                  className={styles.dropdownTimer}
                  placeholder={t('event-clone-modal.placeholder.select-time')}
                  triggerIcon={
                    <Icon
                      iconName="icon_arrow-down"
                      size="xsmall"
                      className={styles.timeIcon}
                    />
                  }
                  hasError={endDateError.endTime}
                />
                {endDateError.endTime && (
                  <p className={styles.errorMessage}>
                    {t('event-clone-modal.error-message.select-valid-time')}
                  </p>
                )}
              </div>
              <IconButton
                iconName="close"
                size="xsmall"
                onClick={() => setShowEndDatePicker(false)}
                className={styles.remove}
              />
            </div>
          ) : (
            <></>
          )}
          {showDeadlinePicker ? (
            <div className={styles.row}>
              <div className={styles.column}>
                <p className={styles.label}>
                  {t('event-clone-modal.registration-deadline.date')}
                </p>
                <DatePicker
                  placeholder={t('event-clone-modal.placeholder.pick-date')}
                  value={deadlineDate || null}
                  onChange={(value) => {
                    setDeadlineDate(value);
                    setDeadlineError({
                      deadlineDate: false,
                      deadlineTime: deadlineError.deadlineTime,
                    });
                  }}
                  className={styles.dropDownCalendar}
                  minDate={new Date()}
                  maxDate={endDate}
                  dateSelectorClassName={styles.dateSelector}
                  hasError={deadlineError.deadlineDate}
                />
                {deadlineError.deadlineDate && (
                  <p className={styles.errorMessage}>
                    {t('event-clone-modal.error-message.pick-valid-date')}
                  </p>
                )}
              </div>
              <div className={styles.column}>
                <p className={styles.label}>
                  {t('event-clone-modal.registration-deadline.time')}
                </p>

                <TimePicker
                  value={deadlineTime}
                  onChange={(value) => {
                    setDeadlineTime(value);
                    setDeadlineError({
                      deadlineDate: deadlineError.deadlineDate,
                      deadlineTime: false,
                    });
                  }}
                  className={styles.dropdownTimer}
                  placeholder={t('event-clone-modal.placeholder.select-time')}
                  triggerIcon={
                    <Icon
                      iconName="icon_arrow-down"
                      size="xsmall"
                      className={styles.timeIcon}
                    />
                  }
                  hasError={deadlineError.deadlineTime}
                />
                {deadlineError.deadlineTime && (
                  <p className={styles.errorMessage}>
                    {t('event-clone-modal.error-message.select-valid-time')}
                  </p>
                )}
              </div>
              <IconButton
                iconName="close"
                size="xsmall"
                onClick={() => setShowDeadlinePicker(false)}
                className={styles.remove}
              />
            </div>
          ) : (
            <></>
          )}

          <div className={styles.timezone}>
            <p className={styles.headingLabel}>
              {t('event-clone-modal.timezone.title')}
            </p>
            <DropdownV2
              label={t('event-clone-modal.timezone.label')}
              title=""
              value={timezone}
              onChange={(value) => setTimezone(value)}
              options={timezoneOptions}
              selectedValueLabel={timezoneLabel}
              isValid={true}
              className={styles.dropdown}
            />
          </div>
          <div className={cn(styles.row, styles.rowButton)}>
            {!showEndDatePicker || !showDeadlinePicker ? (
              <>
                {!showEndDatePicker ? (
                  <ButtonV3
                    size="small"
                    color="secondary"
                    startIcon="plus"
                    onClick={() => setShowEndDatePicker(true)}
                    disabled={!(startDate && startTime)}
                  >
                    {t('event-clone-modal.end-date.add')}
                  </ButtonV3>
                ) : null}
                {!showDeadlinePicker ? (
                  <ButtonV3
                    size="small"
                    color="secondary"
                    startIcon="plus"
                    onClick={() => setShowDeadlinePicker(true)}
                    disabled={!(startDate && startTime)}
                  >
                    {t('event-clone-modal.registration-deadline.add')}
                  </ButtonV3>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
      </div>
      <div className={styles.footer}>
        <div className={styles.separator}></div>
        <div className={styles.section}>
          <ButtonV3 onClick={() => onClose()} color="secondary">
            {t('common.cancel')}
          </ButtonV3>

          <ButtonV3 onClick={onDone} disabled={!startDate || !startTime}>
            {t('common.publish')}
          </ButtonV3>
        </div>
      </div>
    </Modal>
  );
};
